'use client';

// RushMid sound effects — a thin consumer of the app-wide Web Audio engine
// (lib/audioEngine.ts), which owns the AudioContext, buffer registry, gesture-unlock
// and the iOS mute-switch bypass. Here we only keep the RushMid-specific sound LIST,
// the up-front preload gate, and the menu/loading music. We block the RushMid UI
// until every critical sound is decoded, so the runtime path stays deterministic.

import { loadSounds, isLoaded, playLoop, playSfx as enginePlaySfx } from '../../../lib/audioEngine';

// --- RushMid-scoped mute -----------------------------------------------------
// The audio engine is shared (RushMid + case roulette), so the toggle gates only
// RushMid's own sfx + music here, not the whole app. Persisted so it sticks across
// sessions; a tiny subscribe API lets React reflect + drive it (useSyncExternalStore).
const MUTE_KEY = 'rushmid:muted';
// Read guarded: some Android WebViews throw SecurityError on localStorage when site
// data / partitioned storage is blocked — an UNGUARDED read here runs at module eval
// (RushMid mount) and, with no error boundary, would black-screen the whole app.
let muted = false;
try { muted = typeof window !== 'undefined' && window.localStorage.getItem(MUTE_KEY) === '1'; } catch { /* storage blocked */ }
const muteListeners = new Set<() => void>();

export function isMuted(): boolean {
  return muted;
}
export function subscribeMuted(cb: () => void): () => void {
  muteListeners.add(cb);
  return () => muteListeners.delete(cb);
}
export function setMuted(next: boolean): void {
  if (muted === next) return;
  muted = next;
  try { window.localStorage.setItem(MUTE_KEY, next ? '1' : '0'); } catch { /* private mode */ }
  if (muted) stopMusic(); // kill the loop immediately; sfx are gated per-call below
  muteListeners.forEach((cb) => cb());
}

/** Play a RushMid sfx, unless muted. Drop-in replacement for the engine's playSfx. */
export function playSfx(name: string, volume = 1): void {
  if (muted) return;
  enginePlaySfx(name, volume);
}

export const SFX = [
  'timer_beep', 'buttonclickrelease', 'match_found', 'match_win', 'match_lose', 'ct_win', 't_win', 'battle_start',
  'shot_ak', 'shot_m4', 'shot_lmg', 'shot_awp', 'grenade_throw', 'grenade_explode', 'medkit',
  'armor_hit', 'headshot', 'death', 'click', 'knife_deploy', 'knife_stab',
  'smoke_bloom', 'party_join', 'flash_explode', 'grenade_land', 'flash_blind', 'casing_drop',
  'ricochet_1', 'ricochet_2', 'ricochet_3', 'ricochet_4', 'ricochet_5', 'ricochet_6',
  'footstep', 'serdcebienie-normalnoe_zx0xpSKX',
] as const;

/** Ricochet whines — played at random on a MISS (any weapon). */
export const RICOCHET_SFX = ['ricochet_1', 'ricochet_2', 'ricochet_3', 'ricochet_4', 'ricochet_5', 'ricochet_6'] as const;

let preloadPromise: Promise<void> | null = null;

/** Number of discrete sound assets (for a combined load-progress total). */
export const SFX_COUNT = SFX.length;

/** True once every sfx is decoded (sync — to skip the loading screen). */
export function isSfxReady(): boolean {
  return SFX.every((n) => isLoaded(n));
}

/** Decode every sound up-front (call once before showing the RushMid UI).
 * `onUnit` fires once per decoded sound (only on the first, uncached run). */
export function preloadSfx(onUnit?: () => void): Promise<void> {
  if (!preloadPromise) preloadPromise = loadSounds(SFX, onUnit);
  return preloadPromise;
}

// --- looping background music (menu/loading theme) -------------------------
const MUSIC = 'menu_theme';
let stopLoop: (() => void) | null = null;
// bumped by every start/stop; an in-flight startMusic() compares its captured value
// to detect a stopMusic() that raced in during the (async) playLoop() await.
let musicGen = 0;

/** Warm-decode the music track (call early; safe to call repeatedly). */
export function preloadMusic(): Promise<void> {
  return loadSounds([MUSIC]).catch(() => undefined);
}

/** Start the looping theme (no-op if already playing or muted). */
export async function startMusic(volume = 0.35): Promise<void> {
  if (stopLoop || muted) return;
  const gen = ++musicGen;
  const stop = await playLoop(MUSIC, volume);
  if (musicGen !== gen) { stop(); return; } // stopMusic() raced in during the await
  stopLoop = stop;
}

/** Stop the looping theme. */
export function stopMusic(): void {
  musicGen++; // invalidate any in-flight startMusic()
  if (stopLoop) { stopLoop(); stopLoop = null; }
}

// --- music ownership refcount ----------------------------------------------
// RushMid screens (the game + the match-detail stack screen) each claim the theme while
// mounted; it stops only when the LAST claimant releases, after a beat of grace — so a
// stack transition between RushMid screens (history → match detail) never cuts the track,
// while leaving the RushMid section entirely still silences it.
let musicClaims = 0;
let musicReleaseTimer: ReturnType<typeof setTimeout> | null = null;

export function claimMusic(): () => void {
  musicClaims++;
  if (musicReleaseTimer) { clearTimeout(musicReleaseTimer); musicReleaseTimer = null; }
  let released = false;
  return () => {
    if (released) return;
    released = true;
    musicClaims--;
    if (musicClaims <= 0) {
      musicReleaseTimer = setTimeout(() => {
        if (musicClaims <= 0) stopMusic();
      }, 200);
    }
  };
}

// --- looping low-HP heartbeat (local player at <=15 hp) --------------------
const HEARTBEAT = 'serdcebienie-normalnoe_zx0xpSKX';
let stopHeartbeatLoop: (() => void) | null = null;
let heartbeatGen = 0;

/** Start the looping heartbeat (no-op if already playing or muted). */
export async function startHeartbeat(volume = 0.6): Promise<void> {
  if (stopHeartbeatLoop || muted) return;
  const gen = ++heartbeatGen;
  const stop = await playLoop(HEARTBEAT, volume);
  if (heartbeatGen !== gen) { stop(); return; } // stopHeartbeat() raced in during the await
  stopHeartbeatLoop = stop;
}

/** Stop the looping heartbeat. */
export function stopHeartbeat(): void {
  heartbeatGen++; // invalidate any in-flight startHeartbeat()
  if (stopHeartbeatLoop) { stopHeartbeatLoop(); stopHeartbeatLoop = null; }
}
